import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';

const REFRESH_THROTTLE_MS = 10000;
const REFRESH_RETRY_MS = 2000;
const MAX_REFRESH_RETRIES = 15;

// One loop shared by the selected template's Template and Reports views.
export default function useTemplateAutoRefresh({ templateId, fabricId, view, enabled, onAccessLost }) {
  const refreshRef = useRef(null);
  const accessLostRef = useRef(onAccessLost);
  accessLostRef.current = onAccessLost;
  // Last revision each view (per template) has shown, kept across loop restarts
  // so a cached view revalidates only when something changed while it was away.
  const appliedRef = useRef({});
  const kickRef = useRef(null);
  const [runtime, setRuntime] = useState(null);
  const [wakeKey, setWakeKey] = useState(0);
  const wake = useCallback(kind => {
    if (kind === 'report') {
      setRuntime(previous => ({ ...(previous?.template_id === templateId ? previous : {}), template_id: templateId,
        report_gen_status: 'RUNNING', has_active_work: true }));
    }
    setWakeKey(value => value + 1);
  }, [templateId]);
  const registerRefresh = useCallback(callback => {
    refreshRef.current = callback;
    return () => {
      if (refreshRef.current === callback) refreshRef.current = null;
    };
  }, []);
  // The view knows it is behind (e.g. an agent COMPLETED whose response it does not hold):
  // one full fetch through the loop's own throttle, without aborting a fetch in flight.
  const requestRefresh = useCallback(() => kickRef.current?.(), []);

  useEffect(() => {
    // TEMP [STOP-DEBUG]: remove once the stale RUNNING investigation is done
    console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), 'poller effect', { templateId, fabricId, view, enabled, wakeKey });
    if (!enabled || !templateId || !fabricId) return undefined;
    const appliedKey = `${templateId}:${view}`;
    let disposed = false, busy = false, requested = false, denied = false, stopped = false;
    let timer, previousRevision = null, active = false;
    let lastFetchAt = 0, unchanged = 0, errors = 0, refreshRetries = 0;
    const controller = new AbortController();
    const schedule = delay => {
      clearTimeout(timer);
      if (!disposed && !denied && !stopped && !document.hidden) timer = setTimeout(poll, delay);
    };
    const poll = async () => {
      if (disposed || denied || stopped || document.hidden) return;
      if (busy) { requested = true; return; }
      busy = true;
      let delay = null;
      const requestedAt = Date.now();
      try {
        const response = await axios.post('/pceapi/genbi/template-runtime-status', {
          template_id: templateId, fabric_id: fabricId
        }, {
          headers: { Authorization: GetCookie('access_token') },
          signal: controller.signal
        });
        if (disposed) return;
        if (response.data.status === 'NotAuthorized' || response.data.code === 'ACCESS_DENIED') {
          denied = true;
          accessLostRef.current?.();
          return;
        }
        if (response.data.status !== 'success') throw new Error('Runtime status unavailable');
        const data = response.data.data;
        active = Boolean(data.has_active_work);
        setRuntime({ ...data, template_id: templateId, requested_at: requestedAt });
        unchanged = previousRevision === data.revision ? unchanged + 1 : 0;
        previousRevision = data.revision;
        const revision = view === 'report' ? data.report_revision : data.revision;
        // First sighting of this view: its own initial load is current, so this
        // only records a baseline instead of re-fetching the full template.
        const debugFirstSighting = !(appliedKey in appliedRef.current);
        if (!(appliedKey in appliedRef.current)) appliedRef.current[appliedKey] = revision;
        const sinceFetch = Date.now() - lastFetchAt;
        let pending = appliedRef.current[appliedKey] !== revision && !!refreshRef.current;
        console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), 'poll', {
          active, firstSightingBaseline: debugFirstSighting, pending, hasRefreshCallback: !!refreshRef.current,
          sinceFetch, refreshRetries, revisionChanged: unchanged === 0,
        });
        if (pending && sinceFetch >= REFRESH_THROTTLE_MS) {
          // Acknowledge only the revision observed BEFORE the full fetch.
          // Changes committed during that fetch remain pending.
          lastFetchAt = Date.now();
          const applied = await refreshRef.current(controller.signal);
          if (disposed) return;
          if (applied) appliedRef.current[appliedKey] = revision;
          pending = !applied;
          refreshRetries = applied ? 0 : refreshRetries + 1;
          console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), 'poll full refresh', { applied, refreshRetries });
        }
        errors = 0;
        // Never go idle with a change the view has not shown yet (e.g. another fetch was in flight)
        if (pending && refreshRetries <= MAX_REFRESH_RETRIES) {
          delay = sinceFetch < REFRESH_THROTTLE_MS ? REFRESH_THROTTLE_MS - sinceFetch : REFRESH_RETRY_MS;
        } else if (active) {
          delay = Math.min(8000, 3000 + unchanged * 1000);
        } else {
          stopped = true;
          console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), 'poller STOPPED (idle, nothing pending)');
        }
      } catch (error) {
        console.log('[STOP-DEBUG]', new Date().toISOString().slice(11, 23), 'poll ERROR', { message: error?.message, cancelled: axios.isCancel(error), errors: errors + 1 });
        if (disposed || axios.isCancel(error)) return;
        if ([401, 403].includes(error.response?.status)) {
          denied = true;
          accessLostRef.current?.();
          return;
        }
        errors += 1;
        // Retry failures while work is active; once idle give up after a few tries.
        if (!active && errors > 3) { stopped = true; return; }
        delay = Math.min(60000, 5000 * (2 ** Math.min(errors, 4))) + Math.random() * 1000;
      } finally {
        busy = false;
        if (!disposed && requested) { requested = false; schedule(0); }
        else if (delay !== null) schedule(delay);
      }
    };
    const resume = () => {
      if (document.hidden) clearTimeout(timer);
      else { unchanged = 0; schedule(0); }
    };
    kickRef.current = () => {
      if (disposed || denied) return;
      appliedRef.current[appliedKey] = null;
      refreshRetries = 0;
      if (stopped) {
        stopped = false;
        errors = 0;
        schedule(0);
      }
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    schedule(0);
    return () => {
      disposed = true;
      kickRef.current = null;
      clearTimeout(timer);
      controller.abort();
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
    };
  }, [templateId, fabricId, view, enabled, wakeKey]);

  return { runtime: runtime?.template_id === templateId ? runtime : null, wake, registerRefresh, requestRefresh };
}
